import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";

const MODULO = "CobrosSuscripciones";

const normalizarTexto = (texto = "") =>
  String(texto)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

const obtenerId = (valor) => {
  if (!valor) return "";
  if (typeof valor === "object") return valor._id || "";
  return valor;
};

const toInputDate = (valor) => {
  if (!valor) return "";

  const texto = String(valor);

  if (/^\d{4}-\d{2}-\d{2}$/.test(texto)) {
    return texto;
  }

  const fecha = new Date(valor);

  if (Number.isNaN(fecha.getTime())) {
    return "";
  }

  return fecha.toISOString().split("T")[0];
};

const nombreInstructor = (instructor) => {
  if (!instructor || typeof instructor !== "object") return "";

  const nombre = [
    instructor.nombre1Instructor,
    instructor.nombre2Instructor,
    instructor.apellido1Instructor,
    instructor.apellido2Instructor,
  ]
    .filter(Boolean)
    .join(" ")
    .replace(/\s+/g, " ")
    .trim();

  const documento = instructor.documentoInstructor || "Sin documento";

  return `${nombre || "Sin nombre"} - ${documento}`;
};

const leerSesionLocal = () => {
  try {
    const permisosStorage = localStorage.getItem("permisos");
    const usuarioStorage = localStorage.getItem("usuario");
    const userStorage = localStorage.getItem("user");
    const usuariosStorage = localStorage.getItem("usuarios");

    return permisosStorage
      ? JSON.parse(permisosStorage)
      : usuarioStorage
        ? JSON.parse(usuarioStorage)
        : userStorage
          ? JSON.parse(userStorage)
          : usuariosStorage
            ? JSON.parse(usuariosStorage)
            : {};
  } catch (error) {
    console.error("Error leyendo información local:", error);
    return {};
  }
};

const obtenerModulo = (nombreModulo) => {
  try {
    const data = leerSesionLocal();

    const modulos = Array.isArray(data)
      ? data
      : data?.modulos ||
        data?.permisos ||
        data?.usuario?.modulos ||
        data?.usuario?.permisos ||
        data?.user?.modulos ||
        data?.user?.permisos ||
        [];

    const buscado = normalizarTexto(nombreModulo);

    return (
      modulos.find((modulo) => {
        const nombre =
          modulo?.nombreModulo ||
          modulo?.modulo ||
          modulo?.nombre ||
          modulo?.moduloObjectId?.nombreModulo ||
          modulo?.moduloObjectId?.nombre ||
          "";

        return normalizarTexto(nombre) === buscado;
      }) || {}
    );
  } catch (error) {
    console.error("Error leyendo permisos:", error);
    return {};
  }
};

const obtenerRolNombre = () => {
  const data = leerSesionLocal();

  const rol =
    data?.rolObjectId?.nombreRol ||
    data?.rol?.nombreRol ||
    data?.rol?.nombre ||
    data?.rol ||
    data?.role ||
    data?.nombreRol ||
    data?.usuario?.rolObjectId?.nombreRol ||
    data?.usuario?.rol?.nombreRol ||
    data?.usuario?.rol?.nombre ||
    data?.usuario?.rol ||
    data?.user?.rolObjectId?.nombreRol ||
    data?.user?.rol?.nombreRol ||
    data?.user?.rol?.nombre ||
    data?.user?.rol ||
    "";

  return String(rol).trim().toUpperCase();
};


const RegistrarSolicitudesRenovacion = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const edit = Boolean(id);

  const [instructores, setInstructores] = useState([]);
  const [planes, setPlanes] = useState([]);
  const [pagos, setPagos] = useState([]);

  const [instructorObjectId, setInstructorObjectId] = useState("");
  const [planSolicitadoObjectId, setPlanSolicitadoObjectId] = useState("");
  const [pagoObjectId, setPagoObjectId] = useState("");
  const [observacionInstructor, setObservacionInstructor] = useState("");
  const [estadoSolicitudActual, setEstadoSolicitudActual] = useState("");

  const [loading, setLoading] = useState(false);
  const [cargandoBase, setCargandoBase] = useState(false);

  const moduloCobros = useMemo(() => obtenerModulo(MODULO), []);

  const puedeCrear =
    moduloCobros?.puedeCrear === true ||
    moduloCobros?.crear === true ||
    moduloCobros?.create === true;

  const puedeLeer =
    moduloCobros?.puedeLeer === true ||
    moduloCobros?.leer === true ||
    moduloCobros?.read === true;

  const puedeActualizar =
    moduloCobros?.puedeActualizar === true ||
    moduloCobros?.actualizar === true ||
    moduloCobros?.editar === true ||
    moduloCobros?.update === true;

  const rolNombre = useMemo(() => obtenerRolNombre(), []);
  const esInstructor = rolNombre === "INSTRUCTOR";
  const tienePermisoFormulario = edit ? puedeActualizar : puedeCrear;

  const authConfig = () => {
    const token = localStorage.getItem("token");

    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  };

  const manejarErrorSesion = (error) => {
    if (error?.response?.status === 401) {
      Swal.fire({
        icon: "warning",
        title: "Sesión expirada",
        text: "Por favor inicia sesión nuevamente.",
        confirmButtonText: "Aceptar",
      }).then(() => {
        localStorage.removeItem("token");
        navigate("/", { replace: true });
      });

      return true;
    }

    return false;
  };


  const pagosDisponibles = useMemo(() => {
    if (esInstructor || !instructorObjectId) {
      return pagos;
    }

    return pagos.filter((pago) => {
      const instructorId = obtenerId(pago.instructorObjectId);
      return String(instructorId) === String(instructorObjectId);
    });
  }, [pagos, instructorObjectId, esInstructor]);

  const cleanData = () => {
    setInstructorObjectId("");
    setPlanSolicitadoObjectId("");
    setPagoObjectId("");
    setObservacionInstructor("");
    setEstadoSolicitudActual("");
  };

  const handleCancelOrClean = () => {
    if (edit) {
      navigate("/cobros/renovaciones");
    } else {
      cleanData();
    }
  };

  useEffect(() => {
    const cargarBase = async () => {
      try {
        setCargandoBase(true);

        const promesas = [
          axios.get("/api/planes/listall", authConfig()),
          axios.get("/api/pagos/listall", authConfig()),
        ];

        if (!esInstructor) {
          promesas.push(
            axios.get("/api/instructores/listall", authConfig())
          );
        }

        const respuestas = await Promise.all(promesas);

        setPlanes(
          Array.isArray(respuestas[0].data?.planes)
            ? respuestas[0].data.planes
            : []
        );

        setPagos(
          Array.isArray(respuestas[1].data?.pagos)
            ? respuestas[1].data.pagos
            : []
        );

        if (!esInstructor) {
          setInstructores(
            Array.isArray(respuestas[2].data?.instructores)
              ? respuestas[2].data.instructores
              : []
          );
        }
      } catch (error) {
        console.error("Error cargando datos base:", error);

        if (manejarErrorSesion(error)) return;

        Swal.fire(
          "Error",
          "No se pudieron cargar los datos para la renovación.",
          "error"
        );
      } finally {
        setCargandoBase(false);
      }
    };

    cargarBase();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const cargarSolicitud = async () => {
      if (!id) return;

      if (!puedeLeer && !puedeActualizar) {
        Swal.fire(
          "Sin permisos",
          "No tiene permisos para consultar solicitudes de renovación.",
          "warning"
        );
        return;
      }

      try {
        setLoading(true);

        const { data } = await axios.get(
          `/api/renovaciones/listid/${id}`,
          authConfig()
        );

        const solicitud = data?.message;

        if (!data?.ok || !solicitud) {
          throw new Error(data?.message || "No se pudo cargar la solicitud.");
        }

        setInstructorObjectId(obtenerId(solicitud.instructorObjectId));
        setPlanSolicitadoObjectId(
          obtenerId(solicitud.planSolicitadoObjectId)
        );
        setPagoObjectId(obtenerId(solicitud.pagoObjectId));
        setObservacionInstructor(solicitud.observacionInstructor || "");
        setEstadoSolicitudActual(solicitud.estadoSolicitud || "");
      } catch (error) {
        console.error("Error cargando solicitud:", error);

        if (manejarErrorSesion(error)) return;

        Swal.fire(
          "Error",
          error.response?.data?.message ||
            error.message ||
            "No se pudo cargar la solicitud.",
          "error"
        );
      } finally {
        setLoading(false);
      }
    };

    cargarSolicitud();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const actions = async (e) => {
    e.preventDefault();

    if (!tienePermisoFormulario) {
      Swal.fire(
        "Sin permisos",
        edit
          ? "No tiene permisos para actualizar solicitudes de renovación."
          : "No tiene permisos para crear solicitudes de renovación.",
        "warning"
      );
      return;
    }

    if (!esInstructor && !edit && !instructorObjectId) {
      Swal.fire("Atención", "Seleccione un instructor.", "warning");
      return;
    }

    if (!planSolicitadoObjectId) {
      Swal.fire("Atención", "Seleccione el plan solicitado.", "warning");
      return;
    }

    const payload = {
      planSolicitadoObjectId,
      pagoObjectId: pagoObjectId || null,
      observacionInstructor: observacionInstructor.trim(),
    };

    if (!edit && !esInstructor) {
      payload.instructorObjectId = instructorObjectId;
    }

    try {
      setLoading(true);

      const { data } = edit
        ? await axios.put(
            `/api/renovaciones/update/${id}`,
            payload,
            authConfig()
          )
        : await axios.post("/api/renovaciones/add", payload, authConfig());

      await Swal.fire({
        icon: "success",
        title:
          data?.message ||
          (edit
            ? "Solicitud actualizada correctamente"
            : "Solicitud registrada correctamente"),
        showConfirmButton: false,
        timer: 1800,
      });

      navigate("/cobros/renovaciones");
    } catch (error) {
      console.error("Error guardando solicitud de renovación:", error);

      if (manejarErrorSesion(error)) return;

      Swal.fire({
        icon: "error",
        title: edit ? "Error al actualizar" : "Error al registrar",
        text:
          error.response?.data?.message ||
          error.message ||
          "No se pudo guardar la solicitud.",
      });
    } finally {
      setLoading(false);
    }
  };

  if (
    edit &&
    estadoSolicitudActual &&
    estadoSolicitudActual !== "PENDIENTE"
  ) {
    return (
      <div className="container py-5">
        <div className="alert alert-warning">
          Esta solicitud se encuentra en estado{" "}
          <strong>{estadoSolicitudActual}</strong>. Solo las solicitudes
          pendientes pueden editarse.
        </div>
        <button
          className="btn btn-dark"
          onClick={() => navigate("/cobros/renovaciones")}
        >
          Volver
        </button>
      </div>
    );
  }

  if (edit && !puedeActualizar) {
    return (
      <div className="container py-5">
        <div className="alert alert-warning">
          No tiene permisos para actualizar solicitudes de renovación.
        </div>
        <button
          className="btn btn-dark"
          onClick={() => navigate("/cobros/renovaciones")}
        >
          Volver
        </button>
      </div>
    );
  }

  if (!edit && !puedeCrear) {
    return (
      <div className="container py-5">
        <div className="alert alert-warning">
          No tiene permisos para crear solicitudes de renovación.
        </div>
        <button
          className="btn btn-dark"
          onClick={() => navigate("/cobros/renovaciones")}
        >
          Volver
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="container-fluid px-2 px-md-4">
        <div className="bg-white border rounded-4 shadow-sm p-3 p-md-4 mb-3">
          <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
            <div>
              <h4 className="mb-1 fw-semibold">
                {edit
                  ? "Actualizar Solicitud de Renovación"
                  : "Solicitar Renovación"}
              </h4>
              <p className="mb-0 text-secondary small">
                Selecciona el plan deseado y, si existe, asocia el pago
                correspondiente.
              </p>
            </div>

            <div className="d-flex gap-2 flex-wrap">
              <button
                type="button"
                className="btn btn-outline-dark"
                onClick={() => navigate(-1)}
                disabled={loading}
              >
                <i className="fa-solid fa-arrow-rotate-left me-2"></i>
                Volver
              </button>

              <button
                type="button"
                className={`btn ${
                  edit ? "btn-outline-danger" : "btn-outline-dark"
                }`}
                onClick={handleCancelOrClean}
                disabled={loading}
              >
                <i
                  className={`fa-solid ${
                    edit ? "fa-xmark" : "fa-eraser"
                  } me-2`}
                ></i>
                {edit ? "Cancelar" : "Limpiar"}
              </button>
            </div>
          </div>
        </div>

        <form onSubmit={actions}>
          <div className="row g-3">
            <div className="col-12 col-xl-8">
              <div className="bg-white border rounded-4 shadow-sm p-3 p-md-4">
                <h6 className="mb-0 fw-semibold">Solicitud</h6>
                <hr className="my-3" />

                <div className="row g-3">
                  {!esInstructor && (
                    <div className="col-12">
                      <label className="form-label small text-secondary text-uppercase">
                        Instructor *
                      </label>
                      <select
                        className="form-select"
                        value={instructorObjectId}
                        onChange={(e) => {
                          setInstructorObjectId(e.target.value);
                          setPagoObjectId("");
                        }}
                        disabled={edit || cargandoBase}
                        required={!edit}
                      >
                        <option value="">
                          {cargandoBase
                            ? "Cargando instructores..."
                            : "Seleccione"}
                        </option>

                        {instructores.map((instructor) => (
                          <option key={instructor._id} value={instructor._id}>
                            {nombreInstructor(instructor)}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {esInstructor && (
                    <div className="col-12">
                      <div className="alert alert-light border mb-0">
                        <i className="fa-solid fa-user me-2"></i>
                        La solicitud quedará asociada automáticamente a su
                        cuenta.
                      </div>
                    </div>
                  )}

                  <div className="col-12">
                    <label className="form-label small text-secondary text-uppercase">
                      Plan solicitado *
                    </label>
                    <select
                      className="form-select"
                      value={planSolicitadoObjectId}
                      onChange={(e) =>
                        setPlanSolicitadoObjectId(e.target.value)
                      }
                      disabled={cargandoBase}
                      required
                    >
                      <option value="">
                        {cargandoBase ? "Cargando planes..." : "Seleccione"}
                      </option>

                      {planes.map((plan) => (
                        <option key={plan._id} value={plan._id}>
                          {plan.codigoPlan} - {plan.nombrePlan}
                          {plan.estadoPlan === false ? " (INACTIVO)" : ""}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-12">
                    <label className="form-label small text-secondary text-uppercase">
                      Pago asociado
                    </label>
                    <select
                      className="form-select"
                      value={pagoObjectId}
                      onChange={(e) => setPagoObjectId(e.target.value)}
                      disabled={cargandoBase}
                    >
                      <option value="">Sin pago asociado</option>

                      {pagosDisponibles.map((pago) => (
                        <option key={pago._id} value={pago._id}>
                          {toInputDate(pago.fechaPago) || "Sin fecha"} -{" "}
                          {Number(pago.valorPago || 0).toLocaleString("es-CO")}{" "}
                          {pago.moneda || "COP"} - {pago.estadoPago}
                        </option>
                      ))}
                    </select>
                    <div className="form-text">
                      Si el pago todavía está pendiente, deberá aprobarse antes
                      de aprobar la renovación.
                    </div>
                  </div>

                  <div className="col-12">
                    <label className="form-label small text-secondary text-uppercase">
                      Observación
                    </label>
                    <textarea
                      className="form-control"
                      rows="5"
                      value={observacionInstructor}
                      onChange={(e) =>
                        setObservacionInstructor(e.target.value)
                      }
                      placeholder="Información adicional sobre la renovación..."
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12 col-xl-4">
              <div className="bg-white border rounded-4 shadow-sm p-3 p-md-4">
                <h6 className="mb-0 fw-semibold">Proceso</h6>
                <hr className="my-3" />

                <div className="d-flex flex-column gap-3">
                  <div className="border rounded-3 p-3">
                    <div className="small text-secondary">1. Solicitud</div>
                    <div className="fw-semibold">PENDIENTE</div>
                  </div>

                  <div className="border rounded-3 p-3">
                    <div className="small text-secondary">2. Validación</div>
                    <div className="fw-semibold">EN REVISIÓN</div>
                  </div>

                  <div className="border rounded-3 p-3">
                    <div className="small text-secondary">3. Decisión</div>
                    <div className="fw-semibold">APROBADA / RECHAZADA</div>
                  </div>
                </div>

                <div className="alert alert-light border mt-4 mb-0 small text-secondary">
                  Aprobar la solicitud no sustituye la aprobación del pago. El
                  periodo de acceso se crea al aprobar el pago o mediante una
                  suscripción administrativa.
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white border rounded-4 shadow-sm p-3 p-md-4 mt-3">
            <div className="row g-2">
              <div className="col-12 col-md-6">
                <button
                  type="submit"
                  className="btn btn-dark w-100 fw-semibold py-2"
                  disabled={loading || cargandoBase || !tienePermisoFormulario}
                >
                  <i
                    className={`fa-solid ${
                      loading ? "fa-spinner fa-spin" : "fa-floppy-disk"
                    } me-2`}
                  ></i>
                  {loading
                    ? "Guardando…"
                    : edit
                      ? "Guardar cambios"
                      : "Registrar solicitud"}
                </button>
              </div>

              <div className="col-12 col-md-6">
                <button
                  type="button"
                  className={`btn w-100 fw-semibold py-2 ${
                    edit ? "btn-outline-danger" : "btn-outline-secondary"
                  }`}
                  onClick={handleCancelOrClean}
                  disabled={loading}
                >
                  {edit ? "Cancelar" : "Limpiar"}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RegistrarSolicitudesRenovacion;
